import { AD_ATTRIBUTION_FRAGMENT_KEY, decodeAttributionFragment, type AdAttribution } from '@order-up/common';

/**
 * The ad attribution the origin page handed over in the URL fragment
 * (`#handoff=<code>&attr=<base64url>`), kept for the tab so the checkout POST
 * (reached after in-app navigation) still carries it.
 *
 * The visitor's privacy choice is honoured twice: the origin captured
 * `marketingAllowed` when it read the ids, and checkout ANDs it with its own
 * Global Privacy Control signal. When marketing is not allowed, only
 * `{ marketingAllowed: false }` is ever returned — no identifier leaves here.
 */

const STORAGE_PREFIX = 'checkout.attr.';

function storageKey(slug: string): string {
    return `${STORAGE_PREFIX}${slug}`;
}

function gpcActive(): boolean {
    return typeof navigator !== 'undefined' && (navigator as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function readFragmentValue(): string | null {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.hash.replace(/^#/, '')).get(AD_ATTRIBUTION_FRAGMENT_KEY);
}

/** Remove `attr` from the visible URL, leaving every other fragment key (e.g. `handoff`). */
function scrubFragment(): void {
    try {
        if (!window.location.hash) return;
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        if (!fragment.has(AD_ATTRIBUTION_FRAGMENT_KEY)) return;
        fragment.delete(AD_ATTRIBUTION_FRAGMENT_KEY);
        const rest = fragment.toString();
        window.history.replaceState({}, '', window.location.pathname + window.location.search + (rest ? `#${rest}` : ''));
    } catch {
        // History unavailable: the fragment stays, harmless (never sent to a server).
    }
}

/**
 * A stored ALLOWED value older than this is ignored (a later visit is a new
 * attribution). An opt-out never expires: privacy can only narrow, and a lost
 * opt-out would read as "nothing carried".
 */
export const ATTRIBUTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * The page-life copy. Checkout is a SPA, so this survives in-app navigation
 * even when sessionStorage is blocked (private mode), and the fragment is
 * scrubbed only once the value is held here.
 */
const inMemory = new Map<string, { decoded: AdAttribution; savedAt: number }>();

/** One age rule for memory and storage. Decoded FIRST: an expired opt-out is still an opt-out. */
function live(decoded: AdAttribution, savedAt: number): AdAttribution | null {
    if (!decoded.marketingAllowed) return { marketingAllowed: false };
    if (Date.now() - savedAt > ATTRIBUTION_MAX_AGE_MS) return null;
    return decoded;
}

function recall(slug: string): AdAttribution | null {
    const held = inMemory.get(slug);
    if (held) return live(held.decoded, held.savedAt);
    try {
        const raw = window.sessionStorage.getItem(storageKey(slug));
        if (!raw) return null;
        const entry = JSON.parse(raw) as { value?: unknown; savedAt?: unknown };
        if (typeof entry.value !== 'string' || typeof entry.savedAt !== 'number') return null;
        const decoded = decodeAttributionFragment(entry.value);
        return decoded ? live(decoded, entry.savedAt) : null;
    } catch {
        return null;
    }
}

/** Forget this slug's attribution (after a successful order). */
export function clearCheckoutAttribution(slug: string): void {
    inMemory.delete(slug);
    try {
        window.sessionStorage.removeItem(storageKey(slug));
    } catch {
        // Nothing to clear.
    }
}

function remember(slug: string, encoded: string): void {
    try {
        window.sessionStorage.setItem(storageKey(slug), JSON.stringify({ value: encoded, savedAt: Date.now() }));
    } catch {
        // Private mode / blocked storage: attribution is best-effort.
    }
}

/**
 * Read `#attr` once (on page load), remember the encoded value for the tab and
 * take it out of the address bar. Safe to call repeatedly.
 */
export function captureCheckoutAttribution(slug: string): void {
    if (typeof window === 'undefined') return;
    const encoded = readFragmentValue();
    if (encoded === null) return;
    // Only a value that decodes is kept; garbage is dropped, never stored.
    const decoded = decodeAttributionFragment(encoded);
    if (decoded) {
        inMemory.set(slug, { decoded, savedAt: Date.now() });
        remember(slug, encoded);
    }
    // Scrubbed only after the value is held in memory (or was garbage).
    scrubFragment();
}

/** The attribution to send with `POST /checkout`, or undefined when none was carried. */
export function readCheckoutAttribution(slug: string): AdAttribution | undefined {
    if (typeof window === 'undefined') return undefined;
    captureCheckoutAttribution(slug);
    const found = recall(slug);
    if (!found) return undefined;
    if (!found.marketingAllowed || gpcActive()) return { marketingAllowed: false };
    return found;
}
